import {
    ColumnDef,
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { apiFetch } from '../lib/apiFetch';

type TxItem = {
    ID: number;
    txnNumber: string;
    name: string;
    price: number;
    qty: number;
    lineTotal: number;
    // ...other fields if needed
};

type Transaction = {
    id: number;
    txnNumber: string;
    status: string;
    transactionDate: string;
    total: number;
    customer: string;
    paymentMethod: string;
    items: TxItem[];
    // ...other fields if needed
};
const baseurl = import.meta.env.VITE_KOPILIBRASI_URL_PG
const apiUrl = `${baseurl}/transactions`;

const formatCurrency = (v: number) =>
    new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(v);

export default function TransactionsPage() {
    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [loading, setLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState<string>("");
    const [customerFilter, setCustomerFilter] = useState<string>("");
    const [paymentMethodFilter, setPaymentMethodFilter] = useState<string>("");
    const [customers, setCustomers] = useState<string[]>([]);
    const [startDate, setStartDate] = useState<string>(() => {
        const d = new Date();
        d.setMonth(d.getMonth() - 1);
        return d.toISOString().split("T")[0];
    });
    const [endDate, setEndDate] = useState<string>(() => new Date().toISOString().split("T")[0]);
    const [showItemsModal, setShowItemsModal] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
    const [globalFilter, setGlobalFilter] = useState('');

    const columnHelper = createColumnHelper<Transaction>();

    const columns: ColumnDef<Transaction, any>[] = useMemo(() => [
        columnHelper.accessor('txnNumber', {
            header: 'Nomor Transaksi',
            cell: info => <span className="font-medium">{info.getValue()}</span>,
        }),
        columnHelper.accessor('transactionDate', {
            header: 'Tanggal',
            cell: info => (
                <div className="text-gray-700">
                    {info.getValue() ? new Date(info.getValue()).toLocaleString('id-ID', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                    }) : '-'}
                </div>
            ),
        }),
        columnHelper.accessor('status', {
            header: 'Status',
            cell: info => {
                const status = info.getValue()?.toLowerCase();
                return (
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                        status === "paid" 
                            ? "bg-green-100 text-green-800" 
                            : status === "on progress"
                                ? "bg-yellow-100 text-yellow-800"
                                : "bg-red-100 text-red-800"
                    }`}>
                        {info.getValue()}
                    </span>
                );
            },
        }),
        columnHelper.accessor('customer', {
            header: 'Customer',
            cell: info => info.getValue() || '-',
        }),
        columnHelper.accessor('paymentMethod', {
            header: 'Payment Method',
            cell: info => info.getValue() || '-',
        }),
        columnHelper.accessor('total', {
            header: 'Total',
            cell: info => (
                <span className="font-semibold text-right block">
                    {formatCurrency(info.getValue() || 0)}
                </span>
            ),
        }),
        columnHelper.accessor('items', {
            header: 'Items',
            cell: info => (
                <span className="text-center block">
                    {Array.isArray(info.getValue()) ? info.getValue().length : 0}
                </span>
            ),
            enableSorting: false,
        }),
        columnHelper.display({
            id: 'actions',
            header: 'Action',
            cell: ({ row }) => (
                <button
                    onClick={() => {
                        setSelectedTransaction(row.original);
                        setShowItemsModal(true);
                    }}
                    className="px-3 py-1 text-xs font-medium rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200"
                >
                    Detail
                </button>
            ),
        }),
    ], []);

    // reusable fetch, bisa dipanggil dari useEffect dan tombol Display
    const fetchData = async (status?: string, start?: string, end?: string) => {
        setLoading(true);
        try {
            // jika backend nanti support filter, bisa kirim sebagai query
            const params = new URLSearchParams();
            if (status) params.append("status", status);
            // if backend later supports customer filter, can be added here as well
            if (customerFilter) params.append("customer", customerFilter);
            if (paymentMethodFilter) params.append("payment_method", paymentMethodFilter);
            if (start) params.append("datefrom", start);
            if (end) params.append("dateto", end);

            const url = params.toString() ? `${apiUrl}?${params.toString()}` : apiUrl;

            const res = await apiFetch(url);
            const json = await res.json();
            const data: Transaction[] = json.transactions || [];
            setTransactions(data);

            // build unique, sorted customer dropdown: ["", ...uniqueCustomers]
            const uniqueCustomers = Array.from(
                new Set(
                    data
                        .map((t) => t.customer)
                        .filter((c) => c && c.trim().length > 0)
                )
            ).sort((a, b) => a.localeCompare(b));
            setCustomers(["", ...uniqueCustomers]);
        } catch (err) {
            console.error("fetch transactions error", err);
            setTransactions([]);
            setCustomers([""]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // initial load dengan filter default
        fetchData(statusFilter, startDate, endDate);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filtered = useMemo(() => {
        // parse batas tanggal sekali
        const start = startDate ? new Date(startDate + "T00:00:00") : null;
        const end = endDate ? new Date(endDate + "T23:59:59") : null;

        return transactions.filter((t) => {
            if (statusFilter && t.status !== statusFilter) return false;
            // dropdown: jika customerFilter != "" maka harus sama persis
            if (customerFilter && t.customer !== customerFilter) return false;
            // filter payment method bila tersedia di data (misal t.paymentMethod)
            if (paymentMethodFilter) {
                const pm = (t as any).paymentMethod || (t as any).payment_method || "";
                if (pm.toLowerCase() !== paymentMethodFilter.toLowerCase()) return false;
            }

            // jika API punya createdAt ISO, parse ke Date
            let createdDate: Date | null = null;
            if (t.transactionDate) {
                const d = new Date(t.transactionDate);
                if (!isNaN(d.getTime())) createdDate = d;
            }

            if (start && createdDate && createdDate < start) return false;
            if (end && createdDate && createdDate > end) return false;
            return true;
        });
    }, [transactions, statusFilter, customerFilter, paymentMethodFilter, startDate, endDate]);

    // React Table instance
    const table = useReactTable({
        data: filtered,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        state: {
            globalFilter,
        },
        onGlobalFilterChange: setGlobalFilter,
        initialState: {
            pagination: {
                pageSize: 10,
            },
        },
    });

    const stats = useMemo(() => {
        const totalTx = filtered.length;
        const totalRevenue = filtered.filter((t) => t.status.toLowerCase() != "canceled").reduce((s, t) => s + (t.total || 0), 0);
        const paid = filtered.filter((t) => t.status.toLowerCase() === "paid").length;
        const cancel = filtered.filter((t) => t.status.toLowerCase() === "canceled").length;
        return { totalTx, totalRevenue, paid, cancel };
    }, [filtered]);

    const downloadXLSX = () => {
        if (!filtered.length) return;
        // Sheet 1: summary transaksi (tanpa ItemsJSON)
        const txHeaders = ["Nomor Transaksi", "Tanggal Transaksi", "Status", "Customer", "Total", "Total Items"];
        const txRows = filtered.map((t) => {
            const created = t.transactionDate ? new Date(t.transactionDate).toLocaleString() : "";
            const itemsCount = Array.isArray(t.items) ? t.items.length : 0;
            return [t.txnNumber, created, t.status, t.customer, t.total, itemsCount];
        });
        const txAoa = [txHeaders, ...txRows];
        const wsTransactions = XLSX.utils.aoa_to_sheet(txAoa);

        // Sheet 2: detail item per transaksi
        const detailHeaders = [
            "Nomor Transaksi",
            "Tanggal Transaksi",
            "Status",
            "Customer",
            "Nama Item",
            "Harga",
            "Qty",
            "Total Harga",
        ];
        const detailRows: (string | number)[][] = [];

        filtered.forEach((t) => {
            const created = t.transactionDate ? new Date(t.transactionDate).toLocaleString() : "";
            (t.items || []).forEach((item) => {
                detailRows.push([
                    t.txnNumber,
                    created,
                    t.status,
                    t.customer,
                    item.name,
                    item.price,
                    item.qty,
                    item.lineTotal,
                ]);
            });
        });

        const detailAoa = [detailHeaders, ...detailRows];
        const wsDetail = XLSX.utils.aoa_to_sheet(detailAoa);

        // helper: styling header hijau & rows info
        const applyHeaderStyle = (ws: XLSX.WorkSheet, colCount: number) => {
            const headerFill = {
                fill: {
                    patternType: "solid",
                    fgColor: { rgb: "FF22C55E" }, // tailwind green-500
                },
                font: {
                    color: { rgb: "FFFFFFFF" },
                    bold: true,
                },
            };

            // Pastikan !ref ada
            if (!ws["!ref"]) return;
            // tandai row pertama sebagai header
            ws["!rows"] = ws["!rows"] || [];
            ws["!rows"][0] = { ...(ws["!rows"][0] || {}), hpt: 18 }; // tinggi baris opsional

            for (let C = 0; C < colCount; C++) {
                const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
                const cell = ws[cellAddress];
                if (cell) {
                    cell.s = headerFill as any;
                }
            }
        };

        // helper: auto width kolom berdasarkan panjang nilai
        const autoFitColumns = (ws: XLSX.WorkSheet, data: (string | number)[][]) => {
            const colCount = data[0]?.length || 0;
            const colWidths = new Array(colCount).fill(0);

            data.forEach((row) => {
                row.forEach((val, idx) => {
                    const str = val == null ? "" : String(val);
                    // sedikit padding
                    colWidths[idx] = Math.max(colWidths[idx], str.length + 2);
                });
            });

            ws["!cols"] = colWidths.map((w) => ({ wch: w }));
        };

        // apply style & autofit ke kedua sheet
        applyHeaderStyle(wsTransactions, txHeaders.length);
        applyHeaderStyle(wsDetail, detailHeaders.length);
        autoFitColumns(wsTransactions, txAoa);
        autoFitColumns(wsDetail, detailAoa);

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, wsTransactions, "Transactions");
        XLSX.utils.book_append_sheet(wb, wsDetail, "Transaction Detail");

        const fname = `transactions_${new Date().toISOString().slice(0, 10)}.xlsx`;
        XLSX.writeFile(wb, fname);
    };

    const downloadPDF = () => {
        if (!filtered.length) return;

        const doc = new jsPDF({
            orientation: "portrait",
            unit: "pt",
            format: "a4",
        });

        const pageWidth = doc.internal.pageSize.getWidth();
        const leftMargin = 40;
        const rightMargin = 40;
        const contentWidth = pageWidth - leftMargin - rightMargin;

        const pageHeight = doc.internal.pageSize.getHeight();
        const bottomMargin = 40;

        let y = 40;
        filtered.forEach((t) => {
            // Estimasi tinggi yang dibutuhkan untuk satu transaksi
            // Header (garis + tanggal + customer): ~60pt
            // Items: ~20pt per item
            // Footer (item count + subtotal + status + garis): ~80pt
            const txItems = t.items || [];
            const estimatedHeight = 60 + (txItems.length * 20) + 80;

            // Cek apakah perlu halaman baru sebelum mencetak transaksi
            if (y + estimatedHeight > pageHeight - bottomMargin) {
                doc.addPage();
                y = 40;
            }

            const dateStr = t.transactionDate
                ? new Date(t.transactionDate).toLocaleString("id-ID", {
                    day: "2-digit",
                    month: "long",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                })
                : "-";

            // Garis pemisah
            doc.setLineWidth(4.0);
            doc.line(leftMargin, y, pageWidth - rightMargin, y);
            y += 16;

            // Baris tanggal dan label [NOMOR TRANSAKSI]
            doc.setFontSize(11);
            doc.setFont("helvetica", "normal");
            doc.text(dateStr, leftMargin, y);

            const label = t.txnNumber;
            const labelWidth = doc.getTextWidth(label);
            doc.text(label, pageWidth - rightMargin - labelWidth, y);
            y += 20;

            // Label [CUSTOMER]
            doc.setFont("helvetica", "bold");
            doc.text(t.customer, leftMargin, y);
            y += 8;

            // Garis horizontal
            doc.setLineWidth(0.5);
            doc.line(leftMargin, y, pageWidth - rightMargin, y);
            y += 16;

            // Items - pakai autoTable
            const items = t.items || [];

            // Bangun data item seperti contoh:
            // 1. Nama
            //    qty x harga           lineTotal
            const body: any[] = [];
            items.forEach((item, i) => {
                const priceStr = item.price.toLocaleString("id-ID");
                const lineTotalStr = item.lineTotal.toLocaleString("id-ID");
                body.push([
                    `${i + 1}. ${item.name}`,
                    `${item.qty} x ${priceStr}`,
                    lineTotalStr,
                ]);
            });

            autoTable(doc, {
                startY: y,
                body,
                theme: "plain",
                styles: {
                    font: "helvetica",
                    fontSize: 11,
                },
                columnStyles: {
                    0: { cellWidth: contentWidth * 0.5 },
                    1: { cellWidth: contentWidth * 0.25, halign: "left" },
                    2: { cellWidth: contentWidth * 0.25, halign: "right" },
                },
                margin: { left: leftMargin, right: rightMargin },
            });

            // posisi Y setelah tabel
            // @ts-ignore - lastAutoTable ditambahkan oleh plugin
            y = (doc as any).lastAutoTable.finalY + 12;

            // Cek apakah masih ada cukup ruang untuk footer transaksi (~80pt)
            // Jika tidak, buat halaman baru
            if (y + 80 > pageHeight - bottomMargin) {
                doc.addPage();
                y = 40;
            }

            // Garis pemisah
            doc.setLineWidth(0.3);
            doc.line(leftMargin, y, pageWidth - rightMargin, y);
            y += 16;

            // Item count
            doc.setFont("helvetica", "normal");
            doc.text(`Item : ${items.length}`, leftMargin, y);
            y += 16;

            // Total
            const totalLabel = "SubTotal :";
            const totalLabelWidth = doc.getTextWidth(totalLabel);
            const totalValue = `Rp. ${t.total.toLocaleString("id-ID")}`;
            doc.text(totalLabel, leftMargin, y);
            doc.text(totalValue, leftMargin + totalLabelWidth + 10, y);

            // Status (Belum Dibayar / Lunas)
            const statusText =
                t.status.toLowerCase() === "paid" ? "Lunas"
                    : ((t.status.toLowerCase() === "on progress") ? "Belum Bayar" : "Cancel");
            const { r, g, b } =
                t.status.toLowerCase() === "paid"
                    ? { r: 0, g: 128, b: 0 } // green
                    : t.status.toLowerCase() === "on progress"
                        ? { r: 255, g: 165, b: 0 } // yellow / orange
                        : { r: 255, g: 0, b: 0 }; // red

            doc.setTextColor(r, g, b);
            doc.text(statusText, pageWidth - rightMargin - labelWidth, y);
            y += 16;

            // Garis pemisah bawah
            doc.setLineWidth(0.3);
            doc.line(leftMargin, y, pageWidth - rightMargin, y);
            doc.setTextColor(0, 0, 0);
            y += 32;

            // Cek apakah y sudah melewati batas halaman setelah transaksi ini
            if (y > pageHeight - bottomMargin) {
                doc.addPage();
                y = 40;
            }
        });
        // Cek apakah perlu halaman baru untuk bagian total summary
        // Butuh sekitar 150pt untuk garis + 4 baris total + margin
        const requiredHeightForSummary = 150;

        if (y + requiredHeightForSummary > pageHeight - bottomMargin) {
            doc.addPage();
            y = 40;
        }

        doc.setLineWidth(0.3);
        doc.line(leftMargin, y, pageWidth - rightMargin, y);
        doc.setTextColor(0, 0, 0);
        y += 32;
        // Total
        let totalLabel = "Total ALL :";
        let totalLabelWidth = doc.getTextWidth(totalLabel);
        let total = filtered.reduce((s, t) => s + (t.total || 0), 0);
        let totalValue = `Rp. ${total.toLocaleString("id-ID")}`;
        doc.text(totalLabel, leftMargin, y);
        doc.text(totalValue, leftMargin + totalLabelWidth + 10, y);
        y += 16;

        totalLabel = "Total Lunas :";
        totalLabelWidth = doc.getTextWidth(totalLabel);
        total = filtered.filter(t => t.status.toLowerCase() === "paid").reduce((s, t) => s + (t.total || 0), 0);
        totalValue = `Rp. ${total.toLocaleString("id-ID")}`;
        doc.text(totalLabel, leftMargin, y);
        doc.text(totalValue, leftMargin + totalLabelWidth + 10, y);
        y += 16;

        totalLabel = "Total Batal :";
        totalLabelWidth = doc.getTextWidth(totalLabel);
        total = filtered.filter(t => t.status.toLowerCase() === "canceled").reduce((s, t) => s + (t.total || 0), 0);
        totalValue = `Rp. ${total.toLocaleString("id-ID")}`;
        doc.text(totalLabel, leftMargin, y);
        doc.text(totalValue, leftMargin + totalLabelWidth + 10, y);
        y += 16;

        totalLabel = "Total Belum Bayar :";
        totalLabelWidth = doc.getTextWidth(totalLabel);
        total = filtered.filter(t => t.status.toLowerCase() === "on progress").reduce((s, t) => s + (t.total || 0), 0);
        totalValue = `Rp. ${total.toLocaleString("id-ID")}`;
        doc.text(totalLabel, leftMargin, y);
        doc.text(totalValue, leftMargin + totalLabelWidth + 10, y);
        y += 16;

        doc.save(`transactions_${new Date().toISOString().slice(0, 10)}.pdf`);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-3 sm:p-4 md:p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="bg-white rounded-2xl shadow-xl mb-6 md:mb-8 overflow-hidden">
                    <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-4 sm:px-6 md:px-8 py-6 sm:py-8 md:py-12 text-white">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div>
                                <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-1 sm:mb-2">Transactions</h1>
                                <p className="text-blue-100 text-sm sm:text-base md:text-lg">List of recent transactions</p>
                            </div>
                            <div className="text-left sm:text-right">
                                <div className="text-base sm:text-xl md:text-2xl font-bold">
                                    {new Date().toLocaleDateString("en-US", {
                                        weekday: "long",
                                        year: "numeric",
                                        month: "long",
                                        day: "numeric",
                                    })}
                                </div>
                                <div className="text-blue-100 text-sm sm:text-base">{new Date().toLocaleTimeString()}</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mb-6 md:mb-8">
                    <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border-l-4 border-blue-500">
                        <p className="text-xs sm:text-sm font-medium text-gray-500 uppercase tracking-wider">Total Transactions</p>
                        <p className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">{stats.totalTx}</p>
                    </div>
                    <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border-l-4 border-green-500">
                        <p className="text-xs sm:text-sm font-medium text-gray-500 uppercase tracking-wider">Total Revenue</p>
                        <p className="text-lg sm:text-xl md:text-3xl font-bold text-gray-900">{formatCurrency(stats.totalRevenue)}</p>
                    </div>
                    <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border-l-4 border-purple-500">
                        <p className="text-xs sm:text-sm font-medium text-gray-500 uppercase tracking-wider">Paid</p>
                        <p className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">{stats.paid}</p>
                    </div>
                    <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border-l-4 border-orange-500">
                        <p className="text-xs sm:text-sm font-medium text-gray-500 uppercase tracking-wider">Cancel</p>
                        <p className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">{stats.cancel}</p>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-6 md:mb-8">
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-7 gap-3 sm:gap-4 items-center">
                        <div className="flex flex-col justify-center">
                            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Status</label>
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="w-full px-3 sm:px-4 py-2 text-sm border border-gray-300 rounded-lg"
                            >
                                <option value="">All</option>
                                <option value="on progress">On Progress</option>
                                <option value="paid">Paid</option>
                                <option value="canceled">Cancel</option>
                            </select>
                        </div>

                        <div className="flex flex-col justify-center">
                            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Customer</label>
                            <select
                                value={customerFilter}
                                onChange={(e) => setCustomerFilter(e.target.value)}
                                className="w-full px-3 sm:px-4 py-2 text-sm border border-gray-300 rounded-lg"
                            >
                                {customers.map((c, idx) => (
                                    <option key={idx} value={c}>
                                        {c === "" ? "All" : c}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="flex flex-col justify-center">
                            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Payment Method</label>
                            <select
                                value={paymentMethodFilter}
                                onChange={(e) => setPaymentMethodFilter(e.target.value)}
                                className="w-full px-3 sm:px-4 py-2 text-sm border border-gray-300 rounded-lg"
                            >
                                <option value="">All</option>
                                <option value="cash">Cash</option>
                                <option value="qris">Qris</option>
                                <option value="transfer">Transfer</option>
                            </select>
                        </div>

                        <div className="flex flex-col justify-center">
                            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Start Date</label>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className="w-full px-3 sm:px-4 py-2 text-sm border border-gray-300 rounded-lg"
                            />
                        </div>

                        <div className="flex flex-col justify-center">
                            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">End Date</label>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className="w-full px-3 sm:px-4 py-2 text-sm border border-gray-300 rounded-lg"
                            />
                        </div>

                        <div className="col-span-2 sm:col-span-3 md:col-span-2 flex flex-col justify-center">
                            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2 invisible">Action</label>
                            <div className="flex flex-wrap items-center justify-center sm:justify-end gap-2">
                                <button
                                    onClick={() => fetchData(statusFilter, startDate, endDate)}
                                    disabled={loading}
                                    className="flex-1 sm:flex-none bg-gradient-to-r from-green-600 to-green-700 text-white px-3 sm:px-4 py-2 text-sm rounded-lg hover:from-green-700 hover:to-green-800 disabled:opacity-50"
                                >
                                    Display
                                </button>
                                <button
                                    onClick={downloadXLSX}
                                    disabled={loading || filtered.length === 0}
                                    className="flex-1 sm:flex-none bg-gradient-to-r from-blue-600 to-blue-700 text-white px-3 sm:px-4 py-2 text-sm rounded-lg hover:from-blue-700 hover:to-blue-800 disabled:opacity-50"
                                >
                                    Export
                                </button>
                                <button
                                    onClick={downloadPDF}
                                    disabled={loading || filtered.length === 0}
                                    className="flex-1 sm:flex-none bg-gradient-to-r from-red-500 to-red-600 text-white px-3 sm:px-4 py-2 text-sm rounded-lg hover:from-red-600 hover:to-red-700 disabled:opacity-50"
                                >
                                    Download PDF
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50">
                                {table.getHeaderGroups().map(headerGroup => (
                                    <tr key={headerGroup.id}>
                                        {headerGroup.headers.map(header => (
                                            <th
                                                key={header.id}
                                                className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                                onClick={header.column.getToggleSortingHandler()}
                                            >
                                                <div className="flex items-center space-x-2">
                                                    <span>
                                                        {header.isPlaceholder
                                                            ? null
                                                            : flexRender(header.column.columnDef.header, header.getContext())}
                                                    </span>
                                                    {header.column.getIsSorted() && (
                                                        <span className="ml-2">
                                                            {header.column.getIsSorted() === 'desc' ? (
                                                                <ChevronDown className="w-4 h-4" />
                                                            ) : (
                                                                <ChevronUp className="w-4 h-4" />
                                                            )}
                                                        </span>
                                                    )}
                                                </div>
                                            </th>
                                        ))}
                                    </tr>
                                ))}
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {table.getRowModel().rows.map(row => (
                                    <tr key={row.id} className="hover:bg-gray-50 transition-colors duration-150">
                                        {row.getVisibleCells().map(cell => (
                                            <td key={cell.id} className="px-6 py-4 whitespace-nowrap">
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                                {!loading && table.getRowModel().rows.length === 0 && (
                                    <tr>
                                        <td colSpan={8} className="px-6 py-8 text-center text-sm text-gray-500">
                                            Showing 0 results
                                        </td>
                                    </tr>
                                )}
                                {loading && (
                                    <tr>
                                        <td colSpan={8} className="px-6 py-8 text-center text-sm text-gray-500">
                                            Loading...
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="bg-white px-4 sm:px-6 py-4 border-t border-gray-200">
                        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                            <div className="flex items-center space-x-2">
                                <span className="text-xs sm:text-sm text-gray-700">
                                    Showing {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} to{' '}
                                    {Math.min(
                                        (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                                        table.getFilteredRowModel().rows.length
                                    )}{' '}
                                    of {table.getFilteredRowModel().rows.length} results
                                </span>
                            </div>

                            <div className="flex items-center space-x-1 sm:space-x-2">
                                <button
                                    onClick={() => table.setPageIndex(0)}
                                    disabled={!table.getCanPreviousPage()}
                                    className="px-2 sm:px-3 py-1 text-xs sm:text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    First
                                </button>
                                <button
                                    onClick={() => table.previousPage()}
                                    disabled={!table.getCanPreviousPage()}
                                    className="px-2 sm:px-3 py-1 text-xs sm:text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Previous
                                </button>
                                <span className="px-2 sm:px-3 py-1 text-xs sm:text-sm text-gray-700">
                                    Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount() || 1}
                                </span>
                                <button
                                    onClick={() => table.nextPage()}
                                    disabled={!table.getCanNextPage()}
                                    className="px-2 sm:px-3 py-1 text-xs sm:text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Next
                                </button>
                                <button
                                    onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                                    disabled={!table.getCanNextPage()}
                                    className="px-2 sm:px-3 py-1 text-xs sm:text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Last
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Modal Detail Items */}
                {showItemsModal && selectedTransaction && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[80vh] flex flex-col">
                            <div className="flex items-center justify-between p-4 border-b border-slate-200">
                                <div>
                                    <h3 className="text-lg font-semibold text-slate-800">
                                        Transaction - {selectedTransaction.txnNumber}
                                    </h3>
                                    <p className="text-xs text-slate-500">
                                        Customer: {selectedTransaction.customer} •{" "}
                                        {selectedTransaction.transactionDate
                                            ? new Date(selectedTransaction.transactionDate).toLocaleString()
                                            : "-"}
                                    </p>
                                </div>
                                <button
                                    onClick={() => {
                                        setShowItemsModal(false);
                                        setSelectedTransaction(null);
                                    }}
                                    className="px-3 py-1 text-xs rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50"
                                >
                                    Close
                                </button>
                            </div>

                            <div className="p-4 overflow-y-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-3 py-2 text-left font-medium text-gray-500">Name</th>
                                            <th className="px-3 py-2 text-right font-medium text-gray-500">Price</th>
                                            <th className="px-3 py-2 text-center font-medium text-gray-500">Qty</th>
                                            <th className="px-3 py-2 text-right font-medium text-gray-500">Line Total</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {(selectedTransaction.items || []).map((item) => (
                                            <tr key={item.ID}>
                                                <td className="px-3 py-2">{item.name}</td>
                                                <td className="px-3 py-2 text-right">
                                                    {formatCurrency(item.price || 0)}
                                                </td>
                                                <td className="px-3 py-2 text-center">{item.qty}</td>
                                                <td className="px-3 py-2 text-right">
                                                    {formatCurrency(item.lineTotal || 0)}
                                                </td>
                                            </tr>
                                        ))}
                                        {(!selectedTransaction.items || selectedTransaction.items.length === 0) && (
                                            <tr>
                                                <td
                                                    colSpan={4}
                                                    className="px-3 py-4 text-center text-xs text-gray-500"
                                                >
                                                    No items
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            <div className="p-4 border-t border-slate-200 flex justify-end">
                                <button
                                    onClick={() => {
                                        setShowItemsModal(false);
                                        setSelectedTransaction(null);
                                    }}
                                    className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
